import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import API_BASE_URL, { apiUrl } from '../utils/apiBase';

const AuthDebugPage = () => {
  const { user, token, isLoading, isAuthenticated } = useAuth();
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const testAuthEndpoint = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      console.log('Testing auth endpoint with token:', token);
      const response = await authAPI.get('/auth/me');
      setTestResult({
        success: true,
        data: response.data,
        status: response.status
      });
    } catch (error) {
      console.error('Auth test error:', error);
      setTestResult({
        success: false,
        error: {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.response?.data
        }
      });
    } finally {
      setIsTesting(false);
    }
  };

  const testWithoutAuth = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(apiUrl('/auth/me'), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      setTestResult({
        success: response.ok,
        data: data,
        status: response.status
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: {
          message: error.message,
          code: error.code
        }
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">🔧 Auth Debug Page</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Auth Status */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Auth Status</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Authenticated:</span>
                <span className={`font-medium ${isAuthenticated ? 'text-green-600' : 'text-red-600'}`}>
                  {isAuthenticated ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Loading:</span>
                <span className={`font-medium ${isLoading ? 'text-yellow-600' : 'text-gray-600'}`}>
                  {isLoading ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Token:</span>
                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                  {token ? `${token.substring(0, 20)}...` : 'None'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">User:</span>
                <span className="text-sm">
                  {user ? `${user.name} (${user.role})` : 'None'}
                </span>
              </div>
            </div>
          </div>

          {/* API Test */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">API Test</h2>
            <div className="space-y-4">
              <button
                onClick={testAuthEndpoint}
                disabled={isTesting || !token}
                className="w-full btn-primary py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTesting ? 'Testing...' : 'Test /auth/me (with token)'}
              </button>
              
              <button
                onClick={testWithoutAuth}
                disabled={isTesting}
                className="w-full btn-outline py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTesting ? 'Testing...' : 'Test /auth/me (no token)'}
              </button>
            </div>
          </div>
        </div>

        {/* Test Results */}
        {testResult && (
          <div className="mt-6 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Test Results</h2>
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <pre className="text-sm overflow-auto">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Debug Info */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Debug Info</h2>
          <div className="space-y-2 text-sm">
            <div><strong>API Base URL:</strong> {API_BASE_URL}</div>
            <div><strong>Auth API Timeout:</strong> 30 seconds</div>
            <div><strong>Current Time:</strong> {new Date().toLocaleString()}</div>
            <div><strong>User Agent:</strong> {navigator.userAgent}</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => window.location.href = '/login'}
              className="btn-primary py-2 px-4"
            >
              Go to Login
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="btn-outline py-2 px-4"
            >
              Go to Home
            </button>
            <button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="btn-gray py-2 px-4"
            >
              Clear Storage & Reload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthDebugPage;

